import { Email } from "@/types";

// Strip quoted reply from gmail replies
const stripQuotedReply = (body: string): string => {
  if (!body) return body;
  const lines = body.split("\n");
  const cut = findQuoteStartIdx(lines);
  if (cut === -1) return body;
  return lines.slice(0, cut).join("\n").trimEnd();
};

// Find the start of the quotes by identifying familiar patterns
const findQuoteStartIdx = (lines: string[]): number => {
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trimStart();
    if (trimmed.startsWith(">")) return i;
    if (/^-----\s*Original Message\s*-----/i.test(lines[i])) return i;
    if (lines[i].startsWith("On ")) {
      for (let j = 0; j < 4 && i + j < lines.length; j++) {
        if (/wrote:\s*$/.test(lines[i + j])) return i;
      }
    }
  }
  return -1;
};

// Make sure we don't input "RE: " twice
const REPLY_PREFIX_RE = /^re:\s*/i;
const formatReplySubject = (subject: string): string => {
  const trimmed = subject.trim();
  if (REPLY_PREFIX_RE.test(trimmed)) return trimmed;
  return `RE: ${trimmed}`;
};

// Stable key grouping a thread's emails — gmailThreadId, or the email's own
// id when it has none (e.g. a not-yet-threaded send).
const threadKey = (e: Email): string => e.gmailThreadId || e.id;

// Group emails into Gmail threads, oldest thread first, oldest message first
// within each. Threads are keyed by threadKey and ordered by their first
// message's timestamp.
const groupIntoThreads = (emails: Email[]): Email[][] => {
  const byThread = new Map<string, Email[]>();
  for (const e of emails) {
    const key = threadKey(e);
    const group = byThread.get(key);
    if (group) group.push(e);
    else byThread.set(key, [e]);
  }

  const sentTime = (e: Email) => new Date(e.sentAt).getTime();
  const threads = [...byThread.values()].map((group) =>
    [...group].sort((a, b) => sentTime(a) - sentTime(b)),
  );
  return threads.sort((a, b) => sentTime(a[0]) - sentTime(b[0]));
};

export const email = {
  formatReplySubject,
  groupIntoThreads,
  stripQuotedReply,
  threadKey,
};
